// The subdomain/orders#show "Recipients" panel's calendar/reminders drawer and its confirm modal.
// Extracted out of order_recipients_controller.js (R9 - both classes own their own DOM/event
// state - date pickers, reminder sub-flows, a modal's open/close - independent of the Stimulus
// controller's datatable/bulk-selection state).
//
// These are plain JS classes, not Stimulus controllers - element lookups here reach into
// server-rendered content that arrives via a fetch response, outside any Stimulus controller's
// own element tree (contact_drawer.js is the same shape, for the same reason). Neither class can
// declare targets, classes or a Values API, so the plain querySelector and dataset reads below have
// no controller to be scoped to. Teardown is likewise moot - a fresh instance is constructed on every open
// rather than reused (see above), so there is no disconnect() lifecycle to hold it.
//
// A fresh SimpleModal/OrderCalendarDrawer is constructed on every open rather than reused, so
// each instance's listeners are never torn down - they simply stop being reachable once the DOM
// they're bound to is replaced or discarded. That's long-standing accepted behaviour here (see
// the comment on order_recipients_controller.js's openDeleteModal), not something this extraction
// changes.
//
// Both classes keep a jQuery handle (`$element`) alongside the native one for the calls that
// need it: Bootstrap's `.modal()`, the `daterangepicker` plugin, and jQuery's `.fadeOut()` effect
// have no native equivalent worth hand-rolling here.
//
// The R4 findings below (classList calls in buttonLoading/buttonReset/close/open) are a
// decisions.md D2-style trade: buttonLoading/buttonReset used to take an already-jQuery-wrapped
// button and call `.addClass`/`.hide`/`.show`, invisible to the checker either way; now that they
// take a native element (so the drawer-open buttons converted to data-action - R3/R8 - can call
// them directly), the equivalent classList/style calls are visible. No functional change, just
// newly-countable.

import RequestHelper from "./request_helper"

export function fetchJson(url, options) {
  return fetch(url, options).then((response) => response.json().then((data) => ({ ok: response.ok, data })));
}

export function buttonLoading(button, position = "left", size = "") {
  if (!button) { return; }

  button.querySelectorAll("i").forEach((icon) => {
    icon.classList.add("existing-icon");
    icon.style.display = "none";
  });

  const spinner = document.createElement("i");
  const spinnerClass = position === "left" ? `fa fa-spinner spin loading-icon ${size}` : `fa fa-spinner spin icon-right loading-icon ${size}`;
  spinner.className = spinnerClass.trim();

  if (position === "left") {
    button.prepend(spinner);
  } else {
    button.append(spinner);
  }
  button.disabled = true;
}

export function buttonReset(button) {
  if (!button) { return; }

  button.querySelectorAll("i.existing-icon").forEach((icon) => { icon.style.display = ""; });
  button.querySelectorAll("i.loading-icon").forEach((icon) => { icon.remove(); });
  button.disabled = false;
}

// jQuery's `.find()` returns EVERY match and `.on()` binds all of them; `querySelector` returns
// only the first. Both classes below routinely render more than one element per toggle - the
// reminders list has a Remove button per reminder, and a drawer carries both the header close
// icon (_calendar_drawer) and the content partial's Close button - so these bindings have to go
// through querySelectorAll to keep master's behaviour. Binding only the first match is what broke
// orders_spec's "delete the final reminder": the last reminder's Remove button had no listener,
// so #reminder-delete-modal was never opened.
function bindClickAll(root, selector, handler) {
  root?.querySelectorAll(selector).forEach((element) => element.addEventListener("click", handler));
}

// Minimal confirm-modal wrapper, used for order_recipients_controller.js's #gifts-delete-modal
// and, inside OrderCalendarDrawer, for the reminders flow's #reminder-delete-modal. `element` may
// be null - the reminders-list drawer is the only one of the three drawer contents with a
// #reminder-delete-modal in its markup, so the other two construct a SimpleModal around nothing.
// The original jQuery version tolerated this because wrapping null or calling find() on an empty
// jQuery set both degrade to an empty, harmlessly no-op jQuery collection; the
// querySelector/addEventListener calls below are guarded the same way on purpose (see
// patterns.md #1).
export class SimpleModal {
  constructor(element, url, submitForm) {
    this.element = element;
    this.$element = $(element);
    this.name = element ? element.id : undefined;
    this.submitForm = submitForm;
    this.url = url;
    this.submitButtons = element ? Array.from(element.querySelectorAll("[data-toggle='submit']")) : [];
    this.initialize();
  }

  initialize() {
    bindClickAll(this.element, "[data-toggle='close']", () => this.close());
    this.submitButtons.forEach((button) => button.addEventListener("click", () => this.submit()));
  }

  submit() { this.submitForm(this); }
  close() { this.$element.modal("hide"); this.submitReset(); }
  open() { this.$element.modal("show"); }
  submitting() { this.submitButtons.forEach((button) => buttonLoading(button)); }
  submitReset() { this.submitButtons.forEach((button) => buttonReset(button)); }
}

// One class for all three drawers: Expiration and Send Date (the "default" drawer_content
// partial) and Reminders (the add/edit/delete sub-flows), switched on by `type()`/
// `isEditingSendDate`.
//
// Known quirk, left as-is: `preserveCurrentTime` reads `this.dateTimeFormat`, which nothing ever
// assigns - only a same-named local inside `initialize`. So moment() parses with an undefined
// format instead of "MM/DD/YYYY LT". It affects only the send-date flow's time-of-day
// preservation, and no spec covers it.
export default class OrderCalendarDrawer {
  constructor(element, params, callbacks) {
    this.initialize(element, params, callbacks);
  }

  initialize(element, params, callbacks) {
    const shortDateFormat = "MM/DD/YYYY";
    const dateTimeFormat = "MM/DD/YYYY LT";
    // daterangepicker plugin only understands jQuery; the reminders-list drawer content has no
    // ".range-selector input" at all, so this is an empty jQuery set there and every call below
    // that uses it (.daterangepicker(), .click(), .val(), .on()) is a harmless no-op, same as the
    // original.
    const $dateRangePicker = $(element).find(".range-selector input");

    this.element = element;
    this.$element = $(element);
    this.callbacks = callbacks;
    this.params = params;
    this.name = element.id;
    this.$dateRangePicker = $dateRangePicker;
    this.submitForm = callbacks.onSubmitForm;
    this.addReminder = callbacks.onAddReminder;
    this.editReminder = callbacks.onEditReminder;
    this.deleteReminder = callbacks.onDeleteReminder;
    this.isEditingSendDate = this.params.order.type === "scheduled_at";
    this.currentDateFormat = this.isEditingSendDate ? dateTimeFormat : shortDateFormat;
    this.startDate = element.dataset.startDate || moment().format(this.currentDateFormat);
    this.date = this.startDate;
    this.url = element.dataset.url;
    this.backUrl = element.dataset.backUrl;
    this.deleteUrl = element.dataset.deleteUrl;
    this.submitButtons = Array.from(element.querySelectorAll("[data-toggle='submit']:not(.modal [data-toggle='submit'])"));
    this.addButtons = Array.from(element.querySelectorAll("[data-toggle='add']"));
    this.deleteModal = new SimpleModal(element.querySelector("#reminder-delete-modal"), "", () => this.deleteReminder(this));
    this.preservedTime = this.isEditingSendDate ? this.preserveCurrentTime(this.date) : "";

    this.$dateRangePicker.daterangepicker({
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      singleDatePicker: true,
      parentEl: `.${this.name}-datepicker`,
      autoApply: true,
      autoUpdateInput: false,
      startDate: this.startDate,
      minDate: new Date(),
    });

    this.$dateRangePicker.click();
    this.$dateRangePicker.val(this.startDate);

    this.$dateRangePicker.on("apply.daterangepicker", (event, picker) => {
      if (this.isEditingSendDate) {
        this.preservedTime = this.preserveCurrentTime(this.$dateRangePicker.val());
      }
      this.$dateRangePicker.val(`${picker.startDate.format(shortDateFormat)} ${this.preservedTime}`.trim());
      this.date = `${picker.startDate.format(shortDateFormat)} ${this.preservedTime}`;
    });

    this.$dateRangePicker.on("change", (event) => {
      this.date = event.target.value;
      if (this.isEditingSendDate) {
        this.preservedTime = this.preserveCurrentTime(this.date);
      }
    });

    bindClickAll(element, "[data-toggle='close']", () => this.close());
    bindClickAll(element, "[data-toggle='back']", () => this.back());
    bindClickAll(element, "[data-toggle='edit']", (event) => this.edit(event));
    bindClickAll(element, "[data-toggle='delete']", (event) => this.delete(event));
    this.submitButtons.forEach((button) => button.addEventListener("click", () => this.submit()));
    // Drawers with no add button simply have an empty list here, the same harmless no-op the
    // jQuery version got from an empty `.find()` collection.
    this.addButtons.forEach((button) => button.addEventListener("click", () => this.add()));
  }

  type() { return this.name.replace("-drawer", ""); }

  submit() {
    if (!this.validate()) { return; }
    this.submitForm(this);
  }

  validate() {
    if (this.isEditingSendDate) {
      if (!this.validateSendDate()) { return false; }
    }
    return true;
  }

  validateSendDate() {
    const isValid = moment(this.date, "MM/DD/YYYY LT", true).isValid();
    if (!isValid) {
      window.ltAlert({ message: "Please enter a valid date and time.", type: "toast-error", duration: 2500 });
    }
    return isValid;
  }

  preserveCurrentTime(datetime) {
    return moment(datetime, this.dateTimeFormat).format("LT");
  }

  add() { this.addReminder(this); }

  // `.dataset` reads here are kept as jQuery's `.data()` on purpose: `reminder_index` is later
  // used as `drawer.params.reminder_index + 1` to build a `:nth-child()` selector, and
  // `remove_final_reminder` is serialized straight into the PUT/DELETE JSON body. jQuery's
  // `.data()` coerces a purely-numeric attribute to a Number and "true"/"false" to a Boolean;
  // `.dataset` would hand back strings for both, turning that `+ 1` into string concatenation and
  // changing what ships in the request body. Kept on jQuery to preserve those types exactly.
  edit(event) {
    const $button = $(event.currentTarget);
    this.params.remind_at = $button.data("remind-at");
    this.params.reminder_id = $button.data("reminder-id");
    this.params.reminder_index = $button.data("reminder-index");
    this.editReminder(this);
  }

  delete(event) {
    const $button = $(event.currentTarget);
    this.params.reminder_id = $button.data("reminder-id");
    this.params.reminder_index = $button.data("reminder-index");
    this.params.remove_final_reminder = $button.data("remove-final-reminder");
    this.deleteModal.open();
  }

  back() {
    this.params = Object.assign(this.params, { reminders_view: true, use_default_partial: false });
    this.replaceContent(this.backUrl);
  }

  close() {
    this.element.parentElement.classList.remove("active");
    this.element.classList.add("closed");
    this.submitReset();
  }

  open() {
    this.element.parentElement.classList.add("active");
    // Deferred a tick so the CSS transition actually runs.
    setTimeout(() => { this.element.classList.remove("closed"); }, 5);
  }

  submitting() {
    this.submitButtons.forEach((button) => buttonLoading(button));
    this.addButtons.forEach((button) => buttonLoading(button));
  }

  submitReset() {
    this.submitButtons.forEach((button) => buttonReset(button));
    this.addButtons.forEach((button) => buttonReset(button));
  }

  // Always POST: neither caller ever needed another method. Subdomain::OrdersController
  // #calendar_drawer branches on format via `respond_to`, so it needs both the XHR and full
  // Accept headers.
  replaceContent(url, extraParams = {}) {
    this.params = Object.assign(this.params, extraParams);
    this.params.ignore_slide_out = true;

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    });
    options.body = JSON.stringify(this.params);

    fetchJson(url, options).then(({ ok, data }) => {
      if (!ok) {
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        return;
      }
      const wrapper = this.element.closest(`#${this.type()}-wrapper`);
      // Server-rendered, our own origin: Subdomain::OrdersController#calendar_drawer renders
      // subdomain/orders/_calendar_drawer or _calendar_drawer_empty (formats: [:html]), whose
      // drawer_content partials (_default/_next_reminder_at/_next_reminder_at_calendar) carry no
      // recipient-entered data - only selection counts, dates and reminder-schedule text - and
      // none of the five files use raw/html_safe/!=. Confirmed by reading all of them.
      wrapper.innerHTML = data.html;
      this.initialize(wrapper.querySelector(`#${this.name}`), this.params, this.callbacks);
    });
  }

  // Subdomain::Orders::NextReminderAtController#destroy is a bare `render json:` with no format
  // negotiation, so only Content-Type is needed.
  deleteContent(element, callback) {
    const options = RequestHelper.defaultDeleteOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(this.params);

    fetchJson(this.deleteUrl, options).then(({ ok, data }) => {
      if (!ok) {
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        return;
      }
      callback();
      // .fadeOut() is a jQuery animation effect with no native equivalent worth hand-rolling;
      // kept on jQuery for this one call.
      this.$element.find(element).fadeOut();
      this.deleteModal.close();
    });
  }
}
